import {
  Component,
  Host,
  h,
  ComponentInterface,
  Prop,
  Method,
  Watch,
  Event,
  EventEmitter,
  Element,
} from '@stencil/core';

/**
 * @slot  - Children elements of collapse.
 */

@Component({
  tag: 'sy-collapse',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Collapse implements ComponentInterface {
  private eventTrigger: NodeListOf<Element>;

  @Element() el: HTMLSyCollapseElement;

  /**
   * Defines if the collapse component is visible.
   */
  @Prop({ mutable: true, reflect: true }) opened = false;

  /**
   * Set the css selector that will be listened to control the collapse component state.
   */
  @Prop() triggerBy!: string;

  /**
   * Fired when collapse is opened.
   */
  @Event() syCollapseDidOpen: EventEmitter<any>;

  /**
   * Fired when collapse is closed.
   */
  @Event() syCollapseDidClose: EventEmitter<any>;

  @Watch('opened')
  openedWatcher(newValue: boolean, oldValue: boolean): void {
    if (oldValue !== newValue) {
      if (newValue) {
        this.syCollapseDidOpen.emit();
      } else {
        this.syCollapseDidClose.emit();
      }
    }
  }

  /**
   * Open the collapsed component.
   */
  @Method()
  async open(): Promise<void> {
    this.opened = true;
  }

  /**
   * Close the collapsed component.
   */
  @Method()
  async close(): Promise<void> {
    this.opened = false;
  }

  /**
   * Toggle the state of collapsed component.
   */
  @Method()
  async toggle(): Promise<void> {
    this.opened = !this.opened;
  }

  componentDidLoad() {
    const rootNode = this.el.getRootNode() as Element;
    const trigger = rootNode.querySelectorAll(this.triggerBy);

    this.eventTrigger = trigger;
    this.eventTrigger.forEach((el) => {
      el.addEventListener('click', this.toggle.bind(this));
    });
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    this.eventTrigger?.forEach((el) => {
      el.removeEventListener('click', this.toggle.bind(this));
    });
  }

  render() {
    return (
      <Host {...(this.opened ? { opened: true } : null)}>
        <slot></slot>
      </Host>
    );
  }
}
